import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Navigate, NavLink, Outlet } from "react-router-dom";
import type { Branch } from "@dentacore/shared-types";

import { listBranches } from "../api/branches";
import { activeBranch } from "../api/tokens";
import { useActiveBranch } from "../api/use-active-branch";
import { useCanAny } from "../auth/use-can";
import { useModules } from "../shell/modules-context";
import { SETTINGS_NAV } from "./settings-nav";
import styles from "./settings.module.css";

/**
 * Settings, as tabs across the top and the page you picked (UR-D9). Until
 * 2026-10-22 the destinations were a rail down the left; what follows is why
 * there was a rail at all, and still holds for why the pages are split.
 *
 * **What this replaces.** Settings was one scrolling page — 3,119px in an 823px
 * window on a 1440px screen, of which 31 staff rows were most — with four
 * headings on it and five more destinations buried inside a location. Nothing
 * on the index named opening hours, chairs, procedures, prices or block
 * reasons, so the only way to learn they existed was to open a location and
 * scroll past the rest.
 *
 * The rail is the fix, and the reason it is a rail rather than tabs is simply
 * count: nine destinations do not fit across the top of a pane, and a tab strip
 * that scrolls sideways hides exactly what this exists to reveal.
 *
 * ── The location switcher, and why it is not a second source of truth ─────
 *
 * There is **one** active branch in this application: a single id in
 * `localStorage`, which `client.ts` sends as a header on every branch-scoped
 * request. The shell's own switcher writes it, this one writes it, and both
 * subscribe to it through `useActiveBranch` — so they are two views of one
 * value and cannot disagree.
 *
 * **That means changing the location here also changes what the Calendar and
 * Home show**, which is worth saying plainly because it surprised the client
 * when the old screen did it silently: opening a location's page called
 * `activeBranch.set` as a side effect of navigating. This is the same
 * behaviour, made visible and given a label, rather than a new one.
 *
 * A genuinely Settings-local scope would need the branch id to travel per
 * request instead of per session — an API change, and out of scope for a
 * front-end pass. It is written up as an open question rather than smuggled in.
 */

/**
 * The clinic's locations, loaded once for the rail's switcher and shared with
 * the panes that also need them.
 *
 * Here rather than in each pane because the switcher needs the list on every
 * route: three panes fetching the same thing on mount is three requests and
 * three chances for them to disagree about what "closed" means.
 */
interface SettingsScope {
  branches: readonly Branch[] | null;
  /** Re-read the list — after opening, closing or renaming a location. */
  reloadBranches: () => Promise<void>;
  /** The active branch's row, or null while the list or the id is still absent. */
  branch: Branch | null;
}

const ScopeContext = createContext<SettingsScope | null>(null);

/**
 * The labels of System's tabs (UR-D9). A pane whose title only repeats the tab
 * it is under keeps that title for screen readers and drops it from view; a
 * title that names a record, like the location opened, is always drawn.
 */
const TabLabelsContext = createContext<ReadonlySet<string>>(new Set());

export function useTabLabels(): ReadonlySet<string> {
  return useContext(TabLabelsContext);
}

/** The locations and the active one. Only valid inside `SettingsLayout`. */
export function useSettingsScope(): SettingsScope {
  const scope = useContext(ScopeContext);
  if (scope === null) throw new Error("useSettingsScope used outside SettingsLayout");
  return scope;
}

export function SettingsLayout(): React.JSX.Element {
  const [branches, setBranches] = useState<readonly Branch[] | null>(null);
  const canAny = useCanAny();
  const modules = useModules();
  const pages = SETTINGS_NAV.flatMap((group) => group.items).filter(
    (item) => canAny(item.needs) && (item.module === undefined || modules.has(item.module)),
  );
  const active = useActiveBranch();

  const reloadBranches = useCallback(async () => {
    setBranches(await listBranches());
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    listBranches(controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) setBranches(rows);
      })
      // Deliberately silent, and the panes are why: each one reports its own
      // failure against the thing it was asked to show. A toast from the rail
      // would arrive alongside that one saying the same thing twice.
      .catch(() => {
        if (!controller.signal.aborted) setBranches([]);
      });

    return () => controller.abort();
  }, []);

  /**
   * Point the switcher at something as soon as there is something to point at.
   *
   * A first sign-in has no branch in storage, so every scoped pane would show
   * "no location chosen" beside a switcher that was already listing four. The
   * shell does this too; doing it here as well costs one write and means
   * Settings is never the screen that looks broken on day one.
   */
  useEffect(() => {
    if (active !== null || branches === null || branches.length === 0) return;
    const first = branches.find((row) => row.isMember) ?? branches[0];
    if (first !== undefined) activeBranch.set(first.id);
  }, [active, branches]);

  const branch = branches?.find((row) => row.id === active) ?? null;

  return (
    <div className={`${styles.shell} ${styles.shellNoRail}`}>
      {/*
        **System's pages as tabs**, the way the patient file shows its parts
        (Emile, 2026-10-22). Every page this person may open, in the
        menu's order; no section headings, only things that can be clicked.
      */}
      <header className={styles.tabsHead}>
        <h1 className={styles.tabsTitle} data-testid="settings-page">
          System
        </h1>
        <nav className={styles.tabs} aria-label="System">
          {pages.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                isActive ? `${styles.tab} ${styles.tabOn}` : styles.tab
              }
              data-testid={`settings-nav-${item.to}`}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
      </header>

      <TabLabelsContext.Provider value={new Set(pages.map((item) => item.label))}>
        <ScopeContext.Provider value={{ branches, reloadBranches, branch }}>
          <div className={styles.pane}>
            <Outlet />
          </div>
        </ScopeContext.Provider>
      </TabLabelsContext.Provider>
    </div>
  );
}

/**
 * **A System pane shown outside System** — People → Staff since MS-D3 — with the
 * same location scope the panes read, and the same pane frame, minus the rail.
 */
export function StandalonePane({ testId }: { testId: string }): React.JSX.Element {
  const [branches, setBranches] = useState<readonly Branch[] | null>(null);
  const active = useActiveBranch();

  const reloadBranches = useCallback(async () => {
    setBranches(await listBranches());
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    listBranches(controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) setBranches(rows);
      })
      .catch(() => {
        if (!controller.signal.aborted) setBranches([]);
      });
    return () => controller.abort();
  }, []);

  const branch = branches?.find((row) => row.id === active) ?? null;

  return (
    <ScopeContext.Provider value={{ branches, reloadBranches, branch }}>
      <div className={`${styles.pane} ${styles.standalone}`} data-testid={testId}>
        <Outlet />
      </div>
    </ScopeContext.Provider>
  );
}

/**
 * Where `/settings` lands: the first rail item this person may open
 * (STORY-166). It was always Practice details, which a practice manager —
 * who holds locations and staff but not the clinic's own details — would be
 * refused on arrival.
 */
export function SettingsIndex(): React.JSX.Element {
  const canAny = useCanAny();
  const modules = useModules();
  const first = SETTINGS_NAV.flatMap((group) => group.items).find(
    (item) => canAny(item.needs) && (item.module === undefined || modules.has(item.module)),
  );
  return <Navigate to={first?.to ?? "/"} replace />;
}
